import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, ParamMap } from '@angular/router';
import { DisplayService } from './services/display/display.service';
import { NgbConfig, NgbModal, NgbModalRef, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { DeviceService } from './services/device/device.service';
import { CommonService } from './services/common/common.service';
import { LocalStorageKey } from './enums/local-storage-key.enum';
import { WelcomeTutorial } from './data-models/common/welcome-tutorial.model';
import { NetworkStatus } from './enums/network-status.enum';
import { KeyPress } from './data-models/common/key-press.model';
import { VantageShellService } from './services/vantage-shell/vantage-shell.service';
import { SettingsService } from './services/settings.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from './services/language/language.service';
import jsBridgePackageInfo from '@lenovo/tan-client-bridge-le/package.json';
import { DeviceInfo } from './data-models/common/device-info.model';
import { DashboardLocalStorageKey } from './enums/dashboard-local-storage-key.enum';
import { AppNotification } from './data-models/common/app-notification.model';
import { TranslationNotification } from './data-models/translation/translation';
import { LoggerService } from './services/logger/logger.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { VantageFocusHelper } from 'src/app/services/timer/vantage-focus.helper';
import { SegmentConst } from './services/self-select/self-select.service';
import { SessionStorageKey } from './enums/session-storage-key-enum';
import { HistoryManager } from './services/history-manager/history-manager.service';
import { DialogService } from './services/dialog/dialog.service';
import { Overlay } from '@angular/cdk/overlay';
import { MAT_TOOLTIP_SCROLL_STRATEGY } from '@lenovo/material/tooltip';
import { HomeComponent } from './components/home/home.component';
import { containerAppService } from '@lenovo/micro-frontends-iframe/container-app/container-app.service';
import { subAppListForInit } from './micro-frontends/sub-app-config/sub-app-list-for-init';

export const scrollStrategyClose = (overlay: Overlay) => () => overlay.scrollStrategies.close();
const tooltipScrollStrategy = {
	provide: MAT_TOOLTIP_SCROLL_STRATEGY,
	useFactory: scrollStrategyClose,
	deps: [Overlay],
};

declare var Windows;
@Component({
	selector: 'vtr-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	providers: [tooltipScrollStrategy],
})
export class AppComponent implements OnInit, OnDestroy {
	machineInfo: any;
	public isMachineInfoLoaded = false;
	private subscription: Subscription;
	pageTitle = '';
	private totalDuration = 0; // itermittant app duratin will be added to it
	private vantageFocusHelper = new VantageFocusHelper();
	private isServerSwitchEnabled = true;
	private shellVersion;
	private newTutorialVersion = '3.1.2';
	private readonly containerAppName = 'commercialMain';

	constructor(
		private displayService: DisplayService,
		private router: Router,
		private modalService: NgbModal,
		public deviceService: DeviceService,
		private commonService: CommonService,
		private translate: TranslateService,
		private settingsService: SettingsService,
		private vantageShellService: VantageShellService,
		private activatedRoute: ActivatedRoute,
		private languageService: LanguageService,
		private logger: LoggerService,
		private historyManager: HistoryManager,
		private dialogService: DialogService,
		private ngbTooltipConfig: NgbTooltipConfig,
		private ngbConfig: NgbConfig
	) {
		ngbConfig.animation = false;
		this.ngbTooltipConfig.triggers = 'hover';
		this.patchNgbModalOpen();
		// to check web and js bridge version in browser console
		const win: any = window;
		this.shellVersion = this.vantageShellService.getShellVersion();

		win.webAppVersion = {
			web: environment.appVersion,
			bridge: jsBridgePackageInfo.version,
			shell: this.shellVersion,
		};

		// using error because by default its enabled in all
		this.logger.error('APP VERSION', win.webAppVersion);

		this.subscription = this.commonService.notification.subscribe(
			(notification: AppNotification) => {
				this.onNotification(notification);
			}
		);

		//#endregion
		window.addEventListener(
			'online',
			(e) => {
				this.notifyNetworkState();
			},
			false
		);
		window.addEventListener(
			'offline',
			(e) => {
				this.notifyNetworkState();
			},
			false
		);
		this.notifyNetworkState();
		this.addInternetListener();
		this.vantageFocusHelper.start();
		this.initMicroFrontends();
	}

	ngOnInit() {
		const domDoc = document as any;
		if (!domDoc.hasFocus()) {
			domDoc.focus();
		}
		this.getMachineInfo();
		window.onresize = () => {}; // this line is necessary, please do not remove.
		this.checkIsDesktopOrAllInOneMachine();
		this.settingsService.getPreferenceSettingsValue();
		this.setRunVersionToRegistry();
	}

	onDragStart(event: DragEvent): boolean {
		return false;
	}

	onDrop(event: DragEvent): boolean {
		return false;
	}

	ngOnDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	private patchNgbModalOpen() {
		let original = NgbModal.prototype.open;
		NgbModal.prototype.open = function (): NgbModalRef {
			if (arguments.length > 1 && 'container' in arguments[1] === false) {
				Object.assign(arguments[1], { container: 'vtr-root div' });
			}
			return original.apply(this, arguments);
		};
	}

	private addInternetListener() {
		const win: any = window;
		if (win.NetworkListener) {
			win.NetworkListener.onnetworkchanged = (state) => {
				this.notifyNetworkState();
			};

			this.notifyNetworkState();
		} else {
			window.addEventListener(
				'online',
				(e) => {
					this.notifyNetworkState();
				},
				false
			);
			window.addEventListener(
				'offline',
				(e) => {
					this.notifyNetworkState();
				},
				false
			);

			this.notifyNetworkState();
		}
	}

	private async getMachineInfo() {
		if (this.deviceService.isShellAvailable) {
			return this.deviceService
				.getMachineInfo()
				.then((value: any) => {
					this.logger.debug(
						'AppComponent.getMachineInfo received getMachineInfo. is lang loaded: ',
						this.languageService.isLanguageLoaded
					);
					this.onMachineInfoReceived(value);
				})
				.catch((error) => {});
		} else {
			this.isMachineInfoLoaded = true;
			this.machineInfo = { hideMenus: false };
			if (!this.languageService.isLanguageLoaded) {
				this.languageService.useLanguage();
			}
		}
	}

	private onMachineInfoReceived(value: any) {
		this.setFontFamilyByLocale(value.locale);
		const cachedDeviceInfo: DeviceInfo = { locale: value.locale };
		this.commonService.setLocalStorageValue(
			DashboardLocalStorageKey.DeviceInfo,
			cachedDeviceInfo
		);
		this.machineInfo = value;
		this.isMachineInfoLoaded = true;
		this.commonService.sendNotification('MachineInfo', this.machineInfo);
		if (
			!this.languageService.isLanguageLoaded ||
			this.languageService.currentLanguage !== value.locale
				? value.locale.toLowerCase()
				: 'en'
		) {
			this.languageService.useLanguageByLocale(value.locale);
		}
		this.commonService.setLocalStorageValue(LocalStorageKey.MachineFamilyName, value.family);
		this.commonService.setLocalStorageValue(
			LocalStorageKey.SubBrand,
			value.subBrand.toLowerCase()
		);
	}

	private checkIsDesktopOrAllInOneMachine() {
		try {
			if (this.deviceService.isShellAvailable) {
				this.deviceService
					.getMachineType()
					.then((value: any) => {
						this.commonService.setLocalStorageValue(
							LocalStorageKey.DesktopMachine,
							value === 4
						);
						this.commonService.setLocalStorageValue(LocalStorageKey.MachineType, value);
					})
					.catch((error) => {});
			}
		} catch (error) {}
	}

	private notifyNetworkState() {
		this.commonService.isOnline = navigator.onLine;
		if (navigator.onLine) {
			this.commonService.sendNotification(NetworkStatus.Online, {
				isOnline: navigator.onLine,
			});
		} else {
			this.commonService.sendNotification(NetworkStatus.Offline, {
				isOnline: navigator.onLine,
			});
		}
	}

	@HostListener('window:load', ['$event'])
	onLoad(event) {
		const scale = 1 / (window.devicePixelRatio || 1);
		const content = `shrink-to-fit=no, width=device-width, initial-scale=${scale}, minimum-scale=${scale}`;
		document.querySelector('meta[name="viewport"]').setAttribute('content', content);
		// VAN-5872, server switch feature
		// when app loads for the 1st time then remove ServerSwitch values
		window.localStorage.removeItem(LocalStorageKey.ServerSwitchKey);
	}

	@HostListener('window:keydown', ['$event'])
	dialogCtrlA($event: KeyboardEvent) {
		const dialog: HTMLElement = document.querySelector('ngb-modal-window');
		if (dialog && ($event.ctrlKey || $event.metaKey) && $event.keyCode === 65) {
			const activeDom: any = document.activeElement;
			if (
				activeDom &&
				((activeDom.type === 'textarea' && activeDom.tagName === 'TEXTAREA') ||
					(activeDom.type === 'text' && activeDom.tagName === 'INPUT'))
			) {
				return;
			}
			this.selectText(dialog);
			$event.stopPropagation();
			$event.preventDefault();
		}
	}

	selectText(element: any) {
		if ((document.body as any).createTextRange) {
			const range = (document.body as any).createTextRange();
			range.moveToElementText(element);
			range.select();
		} else if (window.getSelection) {
			const selection = window.getSelection();
			const range = document.createRange();
			range.selectNodeContents(element);
			selection.removeAllRanges();
			selection.addRange(range);
		}
	}

	private onNotification(notification: AppNotification) {
		if (notification) {
			switch (notification.type) {
				case TranslationNotification.TranslationLoaded:
					this.logger.info(`AppComponent.onNotification`, notification);
					// launch welcome modal once translation is loaded, meanwhile show spinner from home component
					this.deviceService
						.getMachineInfo()
						.then((info) => {
							if (info) {
								if (
									info.cpuArchitecture &&
									info.cpuArchitecture.toUpperCase().trim() === 'ARM64'
								) {
									const armTutorialData = new WelcomeTutorial(
										2,
										this.newTutorialVersion,
										true,
										SegmentConst.Consumer
									);
									this.commonService.setLocalStorageValue(
										LocalStorageKey.WelcomeTutorial,
										armTutorialData
									);
								}
								const brand = info.brand.toLowerCase();
								const subBrand = info.subBrand.toLowerCase();
								if (
									(brand === 'think' || brand === 'lenovo') &&
									(subBrand === 'thinkcentre' || subBrand === 'thinkcenter')
								) {
									this.commonService.setLocalStorageValue(
										LocalStorageKey.IsDPMSupported,
										true
									);
								} else {
									this.commonService.setLocalStorageValue(
										LocalStorageKey.IsDPMSupported,
										false
									);
								}
							}
						})
						.catch((error) => {});
					this.dialogService.showLicenseAgreementDialog();
					break;
				default:
					break;
			}
		}
	}

	private setRunVersionToRegistry() {
		setTimeout(() => {
			const runVersion = this.shellVersion;
			const regUtil = this.vantageShellService.getRegistryUtil();
			if (runVersion && regUtil) {
				const regPath = 'HKEY_CURRENT_USER\\Software\\Lenovo\\ImController';
				regUtil.queryValue(regPath).then((val) => {
					if (!val || (val.keyList || []).length === 0) {
						return;
					}
					regUtil
						.writeValue(
							regPath + '\\PluginData\\LenovoCompanionAppPlugin\\AutoLaunch',
							'LastRunVersion',
							runVersion,
							'String'
						)
						.then((val2) => {
							if (val2 !== true) {
								this.logger.error('failed to write shell run version to registry');
							}
						});
				});
			}
		}, 2000);
	}

	private setFontFamilyByLocale(locale: string = 'en') {
		const defaultFontFamily = '"Segoe UI", sans-serif';
		let fontFamily = '';
		switch (locale) {
			case 'zh-hans':
				// simplified chinese: full-stop is like english
				fontFamily = `"Microsoft YaHei UI", ${defaultFontFamily}`;
				break;
			case 'zh-hant':
				// traditional chinese: full-stop is in vertically middle
				fontFamily = `"Microsoft JhengHei UI", ${defaultFontFamily}`;
				break;
			case 'ko':
				fontFamily = `"Malgun Gothic", ${defaultFontFamily}`;
				break;
			case 'ja':
				fontFamily = `"Yu Gothic UI", ${defaultFontFamily}`;
				break;
			default:
				fontFamily = defaultFontFamily;
				break;
		}

		// dynamically add font family on body tag
		document.getElementsByTagName('body')[0].style['font-family'] = fontFamily;
		// ngbTooltip is sending font family, to override it dynamically inject css class
		const style = document.createElement<'style'>('style');
		style.innerHTML = `.tooltip { font-family: ${fontFamily}; }`;
		document.getElementsByTagName('head')[0].appendChild(style);
	}

	onActivate(component) {
		if (
			component.constructor.name !== HomeComponent.name &&
			!this.commonService.getSessionStorageValue(SessionStorageKey.FirstPageLoaded, false)
		) {
			this.commonService.setSessionStorageValue(SessionStorageKey.FirstPageLoaded, true);
		}
		this.commonService.scrollTop();
	}
	private initMicroFrontends() {
		containerAppService.initContainerApp(
			this.containerAppName,
			environment.appVersion,
			this.vantageShellService.getVersion()
		);
		containerAppService.initSubAppList(subAppListForInit);
		const routeData = {
			theme: 'dark',
		};

		containerAppService.setRouteDataInAllSubApp(routeData);
	}
}
